// Stop button behavior for an in-flight turn. POSTs the backend cancel
// (which cancels the session's running RunAgent workflow) and unlocks the
// composer optimistically — a Stop that leaves the input dead while the
// server winds down would feel broken. The unlock is safe even if the
// cancel raced a finishing turn: the canonical transcript hydrates
// normally on the next invalidation/poll.

import { useCallback, useState } from 'react';
import { callAgentForge } from '../api/http';
import { useAgentForgeStore } from '../stores';

const cancelTurn = (sessionId: string): Promise<unknown> =>
  callAgentForge({ method: 'POST', url: `/sessions/${sessionId}/cancel` });

export interface StopTurn {
  /** Cancel the active session's in-flight turn and unlock the composer. */
  stop: () => Promise<void>;
  /** True while the cancel POST is in flight. */
  stopping: boolean;
}

export const useStopTurn = (): StopTurn => {
  const [stopping, setStopping] = useState(false);

  const stop = useCallback(async () => {
    const state = useAgentForgeStore.getState();
    const sessionId = state.activeSessionId;
    if (!sessionId || stopping) return;
    setStopping(true);
    try {
      await cancelTurn(sessionId);
    } catch {
      // Even a failed cancel unlocks locally — the turn may already be
      // finished server-side, and a locked composer is the worse outcome.
    } finally {
      const s = useAgentForgeStore.getState();
      s.setTabStatus(sessionId, 'done');
      s.clearStreamingDraft();
      setStopping(false);
    }
  }, [stopping]);

  return { stop, stopping };
};
